import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { FaPlusCircle, FaChevronUp, FaChevronDown, FaEdit, FaTrash } from 'react-icons/fa';
import Loader from '../../Components/utils/Loader';
import { showToast } from '../../Components/utils/ShowToast';
import OfferView from './OfferView'; // Adjust path as per your project structure
import { formatCurrency } from '../../data/constants';
import { LoginContext } from "../../Contexts/LoginContext";

const TradeLeadsTable = () => {
  const navigate = useNavigate();
  const [tradeLeads, setTradeLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedTradelead, setSelectedTradelead] = useState(null);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const { currency, exchangeRate, user } = useContext(LoginContext);
  const socketRef = useRef(null);

  useEffect(() => {
    fetchTradeLeads();
  }, []);

  useEffect(() => {
    if (!user?._id) return;
    const socket = io(process.env.REACT_APP_Backend_Host_Name, {
      auth: { token: localStorage.getItem('token') }
    });
    socketRef.current = socket;

    // When a seller sends a new offer, refresh the buyer's tradeleads
    socket.on('tradeleadOfferRefresh', () => {
      fetchTradeLeads();
    });

    return () => {
      socket.disconnect();
    };
  }, [user?._id]);

  const fetchTradeLeads = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTradeLeads(response.data.tradeLeads);
    } catch (error) {
      showToast('Error fetching tradeleads', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (item) => {
    navigate('/tradeLeadForm', { state: { editData: item } });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tradelead?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTradeLeads(tradeLeads.filter(tl => tl._id !== id));
        showToast('Tradelead deleted successfully', 'success');
      } catch (error) {
        showToast('Error deleting tradelead', 'error');
      }
    }
  };

  const toggleRow = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(id)) newExpandedRows.delete(id);
    else newExpandedRows.add(id);
    setExpandedRows(newExpandedRows);
  };

  const handleViewOffers = (tradelead, offer = null) => {
    setSelectedTradelead(tradelead);
    if (offer) {
      setSelectedOffers([offer]);
    } else {
      setSelectedOffers(tradelead.offers);
    }
  };

  const closeOfferModal = useCallback(() => {
    setSelectedTradelead(null);
    setSelectedOffers([]);
  }, []);

  useEffect(() => {
    if (!selectedTradelead) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setSelectedTradelead(null);
        setSelectedOffers([]);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedTradelead]);

  if (isLoading) return <Loader type="simple" />;

  return (
    <div className="tradeLeadsTableContainer">
      <section className="mainSection">
        <div className="tradeLeadsHeaderBar">
          <h1 className="pageMainHeading">Post Your Requirements</h1>
          <button
            type="button"
            className="primaryBtn  tradeLeadsPostButton"
            onClick={() => navigate('/tradeLeadForm')}
          >
            Post Requirement
          </button>
        </div>
        <table className="tradeLeadsMainTable">
          <thead>
            <tr className="tradeLeadsTableHeader">
              <th>Product</th>
              <th>Category</th>
              <th>Expiry Date</th>
              <th>Quoted Amount</th>
              <th>Received Offers</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tradeLeads.length === 0 ? (
              <div style={{ marginTop: "15px" }}>No tradeleads found</div>
            ) : (
              tradeLeads.map((item) => (
                <React.Fragment key={item._id}>
                  <tr className="tradeLeadsTableRow">
                    <td>
                      <div className="tradeLeadsProductCell">
                        <span className="tradeLeadsProductName singleLineSmallText">{item.product}</span>
                      </div>
                    </td>
                    <td>{item.category}</td>
                    <td>{new Date(item.expiryDate).toLocaleDateString()}</td>
                    <td>{formatCurrency(item.budget, currency, exchangeRate)}</td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {item.offers?.length > 0 ? (
                          <button 
                            style={{ 
                              background: '#eff6ff', 
                              color: '#1d4ed8', 
                              border: '1px solid #bfdbfe', 
                              padding: '6px 16px', 
                              borderRadius: '999px', 
                              fontSize: '13px', 
                              fontWeight: '600', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '8px',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                            onClick={() => toggleRow(item._id)}
                          >
                            <span style={{ 
                              background: '#1d4ed8', 
                              color: 'white', 
                              borderRadius: '50%', 
                              width: '20px', 
                              height: '20px', 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center', 
                              fontSize: '11px' 
                            }}>
                              {item.offers.length}
                            </span>
                            View Offers
                            {expandedRows.has(item._id) ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                          </button>
                        ) : (
                          <span style={{ 
                            background: '#f3f4f6', 
                            color: '#6b7280', 
                            border: '1px solid #e5e7eb', 
                            padding: '6px 16px', 
                            borderRadius: '999px', 
                            fontSize: '13px', 
                            fontWeight: '500', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px' 
                          }}>
                            No Offers Yet
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="tradeLeadsActionButtons">
                        <button className="tradeLeadsEditButton" onClick={() => handleEdit(item)}>
                          <FaEdit size={16} />
                          <span>Edit</span>
                        </button>
                        <button className="tradeLeadsWithdrawButton" onClick={() => handleDelete(item._id)}>
                          <FaTrash size={16} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedRows.has(item._id) && item.offers?.length > 0 && (
                    <tr className="tradeLeadsOffersRow">
                      <td colSpan="6">
                        <div className="tradeLeadsOffersContainer">
                          {item.offers.slice(0, 2).map((offer, index) => (
                            <div key={offer._id} className="tradeLeadsOfferItem">
                              <div className="tradeLeadsOfferInfo">
                                <span className="tradeLeadsOfferNumber">{index + 1}</span>
                                <img
                                  src={offer.seller?.sellerId?.profileImage || '/default-avatar.png'}
                                  alt={offer.seller?.sellerId?.displayName}
                                  className="tradeLeadsOfferAvatar"
                                />
                                <span className="tradeLeadsOfferName">{offer.seller?.sellerId?.displayName}</span>
                                <span className="tradeLeadsOfferAmount">{formatCurrency(offer.quotedAmount, currency, exchangeRate)}</span>
                              </div>
                              <button
                                className="tradeLeadsViewOfferButton"
                                onClick={() => handleViewOffers(item, offer)}
                              >
                                View Offer
                              </button>
                            </div>
                          ))}
                          {item.offers.length > 2 && (
                            <button className="tradeLeadsViewAllButton" onClick={() => handleViewOffers(item)}>
                              View All
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
        <button className="tradeLeadsFloatingActionButton" onClick={() => navigate('/tradeLeadForm')}>
          <FaPlusCircle size={24} />
        </button>
      </section>
      {selectedTradelead && selectedOffers.length > 0 && (
        <div
          className="modalOverlay offerViewModalOverlay"
          onClick={closeOfferModal}
          role="button"
          tabIndex={0}
          aria-label="Close modal"
        >
          <div
            className="offerViewModalContent"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="offer-view-title"
          >
            <div className="offerViewModalHeader">
              <h2 id="offer-view-title" className="offerViewModalTitle">View Offer</h2>
              <button
                type="button"
                className="offerViewModalCloseBtn"
                onClick={closeOfferModal}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="offerViewModalBody">
              <OfferView
                offers={selectedOffers}
                yourAmount={selectedTradelead.budget}
                onClose={closeOfferModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeLeadsTable;